// Sandboxed renderer for untrusted HTML. The iframe runs in an opaque origin (no
// allow-same-origin) so the markup can't reach our DOM, token, or storage, and a
// CSP <meta> is injected so the page can't phone home (connect-src 'none').
// Mirrors the hardening in app/web's features/embedded-html + artifacts.
//
// Presentational: it renders the HTML string it is given. The consumer is
// responsible for loading that string.

const EMBED_CSP =
  "default-src 'none'; script-src 'unsafe-inline'; " +
  "style-src 'unsafe-inline' https://fonts.googleapis.com; font-src https://fonts.gstatic.com; " +
  "img-src data:; connect-src 'none'; base-uri 'none'; form-action 'none'";

// Inject the CSP <meta> as the first thing in <head> so it governs the document
// regardless of the stored markup. Falls back to prepending if there's no <head>.
function withCsp(html: string): string {
  const meta = `<meta http-equiv="Content-Security-Policy" content="${EMBED_CSP}">`;
  if (/<head[^>]*>/i.test(html)) {
    return html.replace(/<head[^>]*>/i, (m) => m + meta);
  }
  return meta + html;
}

export type SandboxedHtmlProps = { html: string; title: string };

/** Render trusted-by-isolation HTML in a hardened, opaque-origin iframe. */
export function SandboxedHtml({ html, title }: SandboxedHtmlProps) {
  return (
    <iframe
      title={title}
      srcDoc={withCsp(html)}
      // No allow-same-origin: opaque origin, no reach into our app. Scripts run;
      // popups let the content's "open in new tab" links work.
      sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
      // Delegate no powerful features (camera, mic, geolocation, …).
      allow=""
      // Don't leak our app URL to the (only) external request, Google Fonts.
      referrerPolicy="no-referrer"
      className="h-full min-h-96 w-full border-0"
    />
  );
}
